/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { RequestStatus } from 'src/graphql/generated/graphql.generated';
import {
  RequesterSummaryRecord,
  RequesterSummaryProps,
} from 'src/store/generated/requester-summary-record.generated';
import {
  RequestLocationSummaryRecord,
  RequestLocationSummaryProps,
} from 'src/store/generated/request-location-summary-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequestSummaryProps {
  id: string;
  status: RequestStatus;
  requester: RequesterSummaryProps | null;
  from: RequestLocationSummaryProps | null;
  to: RequestLocationSummaryProps | null;
  isStandbyRequest: boolean;
  isSisterCompanyReferral: boolean;
  standbyActivationRequested: boolean;
  assignmentDelayedReason: string | null;
  transportTrackingLink: string | null;
  latestWeatherDeclineUnitId: string | null;
  latestWeatherDeclineTime: Date | null;
  latestWeatherDeclineReasonId: string | null;
  latestWeatherDeclineSecondaryReasonId: string | null;
  assignmentDelayedTime: Date | null;
}

export class RequestSummaryRecord extends Record<RequestSummaryProps>({
  id: null as any,
  status: null as any,
  requester: null as any,
  from: null as any,
  to: null as any,
  isStandbyRequest: null as any,
  isSisterCompanyReferral: null as any,
  standbyActivationRequested: null as any,
  assignmentDelayedReason: null as any,
  transportTrackingLink: null as any,
  latestWeatherDeclineUnitId: null as any,
  latestWeatherDeclineTime: null as any,
  latestWeatherDeclineReasonId: null as any,
  latestWeatherDeclineSecondaryReasonId: null as any,
  assignmentDelayedTime: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: RequestStatus;
    requester: any;
    from: any;
    to: any;
    isStandbyRequest: boolean;
    isSisterCompanyReferral: boolean;
    standbyActivationRequested: boolean;
    assignmentDelayedReason: string | null;
    transportTrackingLink: string | null;
    latestWeatherDeclineUnitId: string | null;
    latestWeatherDeclineTime: string | number | Date | null;
    latestWeatherDeclineReasonId: string | null;
    latestWeatherDeclineSecondaryReasonId: string | null;
    assignmentDelayedTime: string | number | Date | null;
  }): RequestSummaryRecord {
    if (json == null) {
      throw new Error('RequestSummaryRecord.fromJS: parameter cannot be null');
    }
    return new RequestSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequestSummaryJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', RequestStatus.CallReceived),
        'RequestSummaryJson.status',
      ),
      requester: json.requester != null ? new RequesterSummaryRecord(json.requester) : null,
      from: json.from != null ? new RequestLocationSummaryRecord(json.from) : null,
      to: json.to != null ? new RequestLocationSummaryRecord(json.to) : null,
      isStandbyRequest: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isStandbyRequest', false),
        'RequestSummaryJson.isStandbyRequest',
      ),
      isSisterCompanyReferral: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSisterCompanyReferral', false),
        'RequestSummaryJson.isSisterCompanyReferral',
      ),
      standbyActivationRequested: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'standbyActivationRequested', false),
        'RequestSummaryJson.standbyActivationRequested',
      ),
      assignmentDelayedReason: json.assignmentDelayedReason,
      transportTrackingLink: json.transportTrackingLink,
      latestWeatherDeclineUnitId: json.latestWeatherDeclineUnitId,
      latestWeatherDeclineTime:
        json.latestWeatherDeclineTime != null ? new Date(json.latestWeatherDeclineTime) : null,
      latestWeatherDeclineReasonId: json.latestWeatherDeclineReasonId,
      latestWeatherDeclineSecondaryReasonId: json.latestWeatherDeclineSecondaryReasonId,
      assignmentDelayedTime:
        json.assignmentDelayedTime != null ? new Date(json.assignmentDelayedTime) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: RequestStatus;
    requester: any;
    from: any;
    to: any;
    isStandbyRequest: boolean;
    isSisterCompanyReferral: boolean;
    standbyActivationRequested: boolean;
    assignmentDelayedReason: string | null;
    transportTrackingLink: string | null;
    latestWeatherDeclineUnitId: string | null;
    latestWeatherDeclineTime: string | number | Date | null;
    latestWeatherDeclineReasonId: string | null;
    latestWeatherDeclineSecondaryReasonId: string | null;
    assignmentDelayedTime: string | number | Date | null;
  }): [RequestSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('RequestSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequestSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequestSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', RequestStatus.CallReceived),
            'RequestSummaryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requester: tryOrDefault(
        () => (json.requester != null ? new RequesterSummaryRecord(json.requester) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      from: tryOrDefault(
        () => (json.from != null ? new RequestLocationSummaryRecord(json.from) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      to: tryOrDefault(
        () => (json.to != null ? new RequestLocationSummaryRecord(json.to) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isStandbyRequest: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isStandbyRequest', false),
            'RequestSummaryJson.isStandbyRequest',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSisterCompanyReferral: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSisterCompanyReferral', false),
            'RequestSummaryJson.isSisterCompanyReferral',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      standbyActivationRequested: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'standbyActivationRequested', false),
            'RequestSummaryJson.standbyActivationRequested',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assignmentDelayedReason: tryOrDefault(
        () => json.assignmentDelayedReason,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportTrackingLink: tryOrDefault(
        () => json.transportTrackingLink,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineUnitId: tryOrDefault(
        () => json.latestWeatherDeclineUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineTime: tryOrDefault(
        () =>
          json.latestWeatherDeclineTime != null ? new Date(json.latestWeatherDeclineTime) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineReasonId: tryOrDefault(
        () => json.latestWeatherDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineSecondaryReasonId: tryOrDefault(
        () => json.latestWeatherDeclineSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assignmentDelayedTime: tryOrDefault(
        () => (json.assignmentDelayedTime != null ? new Date(json.assignmentDelayedTime) : null),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: RequestSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('RequestSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequestSummaryViewModelBase implements RequestSummaryProps {
  protected readonly _props: RequestSummaryProps;
  private _json?: RequestSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get status(): RequestStatus {
    return this._props.status;
  }
  get requester(): RequesterSummaryProps | null {
    return this._props.requester;
  }
  get from(): RequestLocationSummaryProps | null {
    return this._props.from;
  }
  get to(): RequestLocationSummaryProps | null {
    return this._props.to;
  }
  get isStandbyRequest(): boolean {
    return this._props.isStandbyRequest;
  }
  get isSisterCompanyReferral(): boolean {
    return this._props.isSisterCompanyReferral;
  }
  get standbyActivationRequested(): boolean {
    return this._props.standbyActivationRequested;
  }
  get assignmentDelayedReason(): string | null {
    return this._props.assignmentDelayedReason;
  }
  get transportTrackingLink(): string | null {
    return this._props.transportTrackingLink;
  }
  get latestWeatherDeclineUnitId(): string | null {
    return this._props.latestWeatherDeclineUnitId;
  }
  get latestWeatherDeclineTime(): Date | null {
    return this._props.latestWeatherDeclineTime;
  }
  get latestWeatherDeclineReasonId(): string | null {
    return this._props.latestWeatherDeclineReasonId;
  }
  get latestWeatherDeclineSecondaryReasonId(): string | null {
    return this._props.latestWeatherDeclineSecondaryReasonId;
  }
  get assignmentDelayedTime(): Date | null {
    return this._props.assignmentDelayedTime;
  }

  public get isAssigned() {
    return this._props.status === RequestStatus.Assigned;
  }
  public get isAssignmentDelayed() {
    return this._props.status === RequestStatus.AssignmentDelayed;
  }
  public get isCallReceived() {
    return this._props.status === RequestStatus.CallReceived;
  }
  public get isCancelled() {
    return this._props.status === RequestStatus.Cancelled;
  }
  public get isOffered() {
    return this._props.status === RequestStatus.Offered;
  }
  public get isOffering() {
    return this._props.status === RequestStatus.Offering;
  }

  public isAnyStatus(...requestStatus: RequestStatus[]) {
    return requestStatus.includes(this._props.status);
  }

  constructor(props: RequestSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        requester: this._props.requester,
        from: this._props.from,
        to: this._props.to,
        isStandbyRequest: this._props.isStandbyRequest,
        isSisterCompanyReferral: this._props.isSisterCompanyReferral,
        standbyActivationRequested: this._props.standbyActivationRequested,
        assignmentDelayedReason: this._props.assignmentDelayedReason,
        transportTrackingLink: this._props.transportTrackingLink,
        latestWeatherDeclineUnitId: this._props.latestWeatherDeclineUnitId,
        latestWeatherDeclineTime: this._props.latestWeatherDeclineTime,
        latestWeatherDeclineReasonId: this._props.latestWeatherDeclineReasonId,
        latestWeatherDeclineSecondaryReasonId: this._props.latestWeatherDeclineSecondaryReasonId,
        assignmentDelayedTime: this._props.assignmentDelayedTime,
      };
    }
    return this._json;
  }
}
